import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { HttpStatus } from 'enums'
import { PortfolioViewState } from 'enums/PortfolioViewState'
import { Portfolio } from 'types'

import { paveApi } from 'api'
import ModalLoader from 'components/common/Loader/ModalLoader'
import ConfirmDeletePortfolioModal from 'components/partials/Portfolio/V2/Modals/ConfirmDeletePortfolioModal'
import EditPortfolioSettingsModal from 'components/partials/Portfolio/V2/Modals/EditPortfolioSettingsModal'
import PortfolioAllocationCard from 'components/partials/Portfolio/V2/PortfolioAllocationCard'
import PortfolioCashCard from 'components/partials/Portfolio/V2/PortfolioCashCard'
import PortfolioHeaderSection from 'components/partials/Portfolio/V2/PortfolioHeaderSection'
import PortfolioInvestmentPreferencesCard from 'components/partials/Portfolio/V2/PortfolioInvestmentPreferencesCard'
import PortfolioPerformanceCard from 'components/partials/Portfolio/V2/PortfolioPerformanceCard'
import RequestSupportModal from 'components/partials/RequestSupportModal'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import { SIZE } from 'designSystem/foundations/constants/size'
import { useCustomToast } from 'hooks/useCustomToast'
import {
  getIsAccountDisconnected,
  getIsConnectedAccount,
  getPortfolioLinkType,
  getPortfolioViewState,
  shouldLockPortfolio,
} from 'utils/portfolioUtil'

import PortfolioAssetsCard from './PortfolioAssetsCard'

type Props = {
  portfolio: Portfolio
}

export default function PortfolioPageContent({ portfolio }: Props) {
  const { showErrorToast, showSuccessToast, showViewOnlyDefaultToast } =
    useCustomToast()
  const navigate = useNavigate()
  const [showEditGeneralSettingsModal, setShowEditGeneralSettingsModal] =
    useState(false)
  const [showDeletePortfolioModal, setShowDeletePortfolioModal] =
    useState(false)
  const [showRequestSupportModal, setShowRequestSupportModal] = useState(false)

  const [deletePortfolio, { isLoading: isDeletePortfolioLoading }] =
    paveApi.useDeletePortfolioMutation()

  const { accountStatus } = portfolio
  const portfolioValue = portfolio?.value ?? 0
  const isPortfolioDisconnected = getIsAccountDisconnected(
    portfolio?.accountStatus
  )

  const linkType = getPortfolioLinkType(portfolio)
  const isConnectedAccount = getIsConnectedAccount(linkType)
  const isLocked = shouldLockPortfolio(portfolio)

  const menuItems = [
    {
      action: () => setShowEditGeneralSettingsModal(true),
      name: 'Portfolio Settings',
    },
    {
      action: () => navigate(`/investment-preference/${portfolio?.id}`),
      name: 'Investment Preferences',
    },
    {
      action: () => navigate(`/account/${portfolio?.clientAccountId || ''}`),
      name: 'Go To Group',
    },
  ]

  if (
    getPortfolioViewState(portfolio) !== PortfolioViewState.NORMAL ||
    isLocked
  ) {
    menuItems.splice(1, 1)
  }

  function confirmDeletePortfolio() {
    deletePortfolio(portfolio?.id || '')
      .unwrap()
      .then(() => {
        setShowDeletePortfolioModal(false)
        showSuccessToast('Portfolio deleted!')
        navigate(`/account/${portfolio?.clientAccountId}`)
      })
      .catch((e) => {
        if (e?.status === HttpStatus.FORBIDDEN) {
          showViewOnlyDefaultToast()
        } else {
          showErrorToast('An error occurred deleting your portfolio')
        }
      })
  }

  return (
    <>
      <PortfolioHeaderSection
        portfolio={portfolio}
        menuItems={menuItems}
        openRequestSupport={() => setShowRequestSupportModal(true)}
        openDeletePortfolioModal={() => setShowDeletePortfolioModal(true)}
        linkType={linkType}
        accountStatus={accountStatus}
      />

      <PDSFlexBox gap={SIZE.s20}>
        <PDSFlexBox flex={4} flexDirection="column" gap={SIZE.s20}>
          <PortfolioPerformanceCard portfolio={portfolio} />

          <PortfolioAllocationCard portfolio={portfolio} />

          <PortfolioAssetsCard portfolio={portfolio} />
        </PDSFlexBox>

        <PDSFlexBox
          flex={3}
          flexDirection="column"
          gap={SIZE.s20}
          maxWidth="24rem"
        >
          <PortfolioInvestmentPreferencesCard portfolio={portfolio} />
          <PortfolioCashCard portfolio={portfolio} />
        </PDSFlexBox>
      </PDSFlexBox>
      <EditPortfolioSettingsModal
        closeModal={() => setShowEditGeneralSettingsModal(false)}
        portfolio={portfolio}
        showDeletePortfolioModal={() => setShowDeletePortfolioModal(true)}
        showModal={showEditGeneralSettingsModal}
      />
      <RequestSupportModal
        entryPoint="Portfolio Page"
        isOpen={showRequestSupportModal}
        onClose={() => setShowRequestSupportModal(false)}
        portfolioId={portfolio?.id}
        daysUntilSuggestions={portfolio?.daysUntilSuggestions || 0}
        incompletePortfolio={
          portfolioValue === 0 ||
          isPortfolioDisconnected ||
          !portfolio?.isInvestmentPreferenceCompleted
        }
      />
      <ConfirmDeletePortfolioModal
        showModal={showDeletePortfolioModal}
        hideModal={() => setShowDeletePortfolioModal(false)}
        onClick={confirmDeletePortfolio}
        isConnectedAccount={isConnectedAccount}
      />
      {isDeletePortfolioLoading && <ModalLoader />}
    </>
  )
}
